import React, { useState, useEffect, useCallback, createContext, useContext } from "react";
import axios from "axios";
import { useDispatch } from "react-redux";
import { setUser, setAuthToken } from "../features/auth/authSlice";

const KeycloakContext = createContext();

export const useKeycloak = () => {
  const context = useContext(KeycloakContext);
  if (!context) {
    throw new Error("useKeycloak must be used within KeycloakProvider");
  }
  return context;
};

const KeycloakProvider = ({ children }) => {
  const dispatch = useDispatch();

  const [auth, setAuth] = useState({
    authenticated: false,
    token: null,
    user: null,
    initialized: false
  });

  const backendUrl = process.env.REACT_APP_ROOT_URL_SUPERVISION_BACK;
  const parseJwt = (token) => {
    try {
      const base64Url = token.split(".")[1];
      const base64 = base64Url.replace(/-/g, "+").replace(/_/g, "/");
      const jsonPayload = decodeURIComponent(
        atob(base64)
          .split("")
          .map((c) => "%" + ("00" + c.charCodeAt(0).toString(16)).slice(-2))
          .join("")
      );
      return JSON.parse(jsonPayload);
    } catch (e) {
      return null;
    }
  };

  const login = () => {
    window.location.href = `${backendUrl}/keycloak/login`;
  };

  const logout = async () => {
    const refreshToken = localStorage.getItem("keycloak_refresh_token");

    if (refreshToken) {
      try {
        await axios.post(`${backendUrl}/keycloak/logout`, { refresh_token: refreshToken });
      } catch (error) {}
    }

    localStorage.removeItem("keycloak_token");
    localStorage.removeItem("keycloak_refresh_token");
    delete axios.defaults.headers.common["Authorization"];

    dispatch(setUser(null));
    dispatch(setAuthToken(null));

    setAuth({
      authenticated: false,
      token: null,
      user: null,
      initialized: true
    });

    window.location.href = "/";
  };

  const updateToken = async (minValidity = 30) => {
    if (!auth.token) return null;

    const tokenParsed = parseJwt(auth.token);
    if (!tokenParsed) return null;

    const expiresIn = tokenParsed.exp - Math.floor(Date.now() / 1000);

    if (expiresIn < minValidity) {
      const refreshToken = localStorage.getItem("keycloak_refresh_token");

      if (!refreshToken) {
        logout();
        return null;
      }

      try {
        const response = await axios.post(`${backendUrl}/keycloak/refresh`, {
          refresh_token: refreshToken
        });

        const { access_token, refresh_token: newRefreshToken } = response.data;

        localStorage.setItem("keycloak_token", access_token);
        localStorage.setItem("keycloak_refresh_token", newRefreshToken);

        setAuth((prev) => ({ ...prev, token: access_token }));
        axios.defaults.headers.common["Authorization"] = `Bearer ${access_token}`;

        return access_token;
      } catch (error) {
        logout();
        return null;
      }
    }

    return auth.token;
  };

  const extractUserInfo = (tokenParsed) => ({
    id: tokenParsed?.sub,
    email: tokenParsed?.email,
    name: tokenParsed?.name || tokenParsed?.preferred_username,
    username: tokenParsed?.preferred_username,
    roles: tokenParsed?.resource_access?.["biotech-supervision"]?.roles || tokenParsed?.resource_access?.["biotech-access-api"]?.roles || [],
    realmRoles: tokenParsed?.realm_access?.roles || [],
    hasItRole: tokenParsed?.realm_access?.roles?.includes("it") || false
  });

  const setupAuthenticatedUser = useCallback(
    (token, refreshToken) => {
      const tokenParsed = parseJwt(token);
      const userInfo = extractUserInfo(tokenParsed);

      localStorage.setItem("keycloak_token", token);
      if (refreshToken) {
        localStorage.setItem("keycloak_refresh_token", refreshToken);
      }

      dispatch(setUser(userInfo));
      dispatch(setAuthToken(token));

      setAuth({
        authenticated: true,
        token,
        user: userInfo,
        initialized: true
      });

      axios.defaults.headers.common["Authorization"] = `Bearer ${token}`;
    },
    [dispatch]
  );

  useEffect(() => {
    const init = () => {
      const urlParams = new URLSearchParams(window.location.search);
      const tokenFromUrl = urlParams.get("token");
      const refreshTokenFromUrl = urlParams.get("refresh_token");

      if (tokenFromUrl) {
        setupAuthenticatedUser(tokenFromUrl, refreshTokenFromUrl);
        window.history.replaceState({}, document.title, window.location.pathname);
        return;
      }

      const storedToken = localStorage.getItem("keycloak_token");
      if (storedToken) {
        const tokenParsed = parseJwt(storedToken);
        const currentTime = Math.floor(Date.now() / 1000);

        if (tokenParsed?.exp && tokenParsed.exp > currentTime) {
          const storedRefreshToken = localStorage.getItem("keycloak_refresh_token");
          setupAuthenticatedUser(storedToken, storedRefreshToken);
          return;
        } else {
          localStorage.removeItem("keycloak_token");
          localStorage.removeItem("keycloak_refresh_token");
        }
      }

      setAuth((prev) => ({ ...prev, initialized: true }));
    };

    init();
  }, [setupAuthenticatedUser]);

  const keycloak = {
    authenticated: auth.authenticated,
    token: auth.token,
    tokenParsed: auth.token ? parseJwt(auth.token) : null,
    userInfo: auth.user,
    initialized: auth.initialized,
    login,
    logout,
    updateToken
  };

  return <KeycloakContext.Provider value={{ keycloak, initialized: auth.initialized }}>{children}</KeycloakContext.Provider>;
};

export default KeycloakProvider;
